<template>
  <div
    v-if="appStore.withdrawModal"
    id="withdrawModal"
    class="fixed inset-0 z-50 bg-black bg-opacity-80 flex items-center justify-center p-4"
  >
    <div
      class="w-full max-w-md rounded-2xl p-6 shadow-2xl bg-[#0C0E1B] border border-orange-500 relative text-white"
    >
      <button
        class="absolute top-3 right-3 text-white text-xl hover:text-gray-400"
        @click="closeModal"
      >
        ✕
      </button>

      <div class="flex items-center gap-2 mb-2">
        <span class="text-green-400 text-xl">$</span>
        <h2 class="text-2xl font-bold">Solicitar Saque</h2>
      </div>
      <p class="text-gray-400 text-sm mb-6">
        Escolha o valor e a moeda que deseja sacar.
      </p>

      <div class="space-y-4">
        <VaInput
          v-model="valor"
          type="number"
          label="Valor do Saque"
          color="#F97316"
          class="w-full"
          :rules="[(v) => Number(v) > 0 || 'Informe um valor válido']"
          :disabled="isLoading"
        />

        <VaSelect
          v-model="moeda"
          label="Moeda"
          class="w-full"
          color="#F97316"
          :options="['BRL']"
          :disabled="isLoading"
        />

        <!-- CHAVE PIX DE DESTINO -->
        <VaSelect
          v-model="selectedPixKeyId"
          label="Chave PIX de Destino"
          class="w-full"
          color="#F97316"
          :options="pixKeyOptions"
          :disabled="isLoading"
          :placeholder="
            pixKeyOptions.length
              ? 'Selecione uma chave'
              : 'Nenhuma chave cadastrada'
          "
          :text-by="'text'"
          :value-by="'value'"
          :track-by="'value'"
        />

        <!-- link adicionar -->
        <div class="text-left">
          <button
            type="button"
            class="text-xs text-purple-300 hover:text-purple-200 underline inline-flex items-center gap-1"
            @click="addNewPixForm()"
          >
            <span class="text-purple-300">＋</span> Adicionar nova chave
          </button>
        </div>

        <!-- MÚLTIPLAS NOVAS CHAVES PIX -->
        <div
          v-for="(form, idx) in newPixForms"
          :key="form.uid"
          class="rounded-xl border border-orange-500/40 p-4 bg-[#0F1222] space-y-3"
        >
          <div class="flex items-center justify-between">
            <h3 class="font-semibold text-sm opacity-90">Nova Chave PIX</h3>
            <button
              class="text-xs text-gray-400 hover:text-gray-200"
              :disabled="isLoading"
              @click="removePixForm(idx)"
            >
              Remover
            </button>
          </div>

          <div class="grid grid-cols-2 gap-3">
            <VaSelect
              v-model="form.type"
              label="Tipo de Chave"
              color="#F97316"
              :options="pixTypeOptions"
              :disabled="isLoading"
              :text-by="'text'"
              :value-by="'value'"
              :track-by="'value'"
            />
            <VaInput
              v-model="form.key"
              :label="form.type === 'phone' ? 'Chave (Com DDI +55)' : 'Chave'"
              placeholder="Insira a sua chave"
              color="#F97316"
              :disabled="isLoading || form.type === 'random'"
            />
          </div>

          <VaButton
            color="#7C3AED"
            class="w-full text-white font-semibold rounded-lg py-2"
            :loading="isLoading"
            @click="savePixKey(idx)"
          >
            Salvar Chave
          </VaButton>
        </div>

        <VaButton
          color="#F97316"
          class="w-full text-white font-semibold rounded-lg py-3"
          :loading="isLoading"
          @click="solicitarSaque"
        >
          {{ isLoading ? "Carregando..." : "Confirmar Saque" }}
        </VaButton>
      </div>

      <p v-if="erro" class="text-red-500 text-sm mt-4 text-center">
        {{ erro }}
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useToast } from "vuestic-ui";
import { useWithdrawalStore } from "@/stores/withdrawal";
import { useAuthStore } from "@/stores/auth";
import { useAppStore } from "@/stores/integration";
import { useFinancialStore } from "@/stores/financial";

const appStore = useAppStore();
const withdrawalStore = useWithdrawalStore();
const authStore = useAuthStore();
const financialStore = useFinancialStore();
const { init } = useToast();

const valor = ref(0);
const moeda = ref("BRL");
const erro = ref("");
const tax = ref(0);

const isLoading = computed(
  () => withdrawalStore.isLoading || financialStore.loading,
);

// ===== PIX =====
const selectedPixKeyId = ref<string | number | null>(null);

// Formulários dinâmicos para novas chaves
type NewPixForm = {
  uid: string;
  type: "email" | "cpf" | "phone" | "random";
  key: string;
};
const newPixForms = ref<NewPixForm[]>([]);

// opções de tipo (labels iguais ao print)
const pixTypeOptions = [
  { text: "CPF", value: "cpf" },
  { text: "E-mail", value: "email" },
  { text: "Telefone", value: "phone" },
  { text: "Aleatória", value: "random" },
];

// mapeia chaves existentes para o VaSelect
const pixKeyOptions = computed(() => {
  const keys = (financialStore.pixKeys as any[]) || [];
  return keys.map((k) => ({
    text: `${(k.type || "").toUpperCase()}: ${k.key}`,
    value: k.id,
  }));
});

const uid = () => Math.random().toString(36).slice(2);

function addNewPixForm() {
  newPixForms.value.push({ uid: uid(), type: "cpf", key: "" });
}

// remove card
function removePixForm(index: number) {
  newPixForms.value.splice(index, 1);
}

async function savePixKey(index: number) {
  erro.value = "";
  const form = newPixForms.value[index];
  if (!form) return;

  const type =
    typeof form.type === "object" && form.type !== null
      ? (form.type as any).value
      : form.type;

  if (type !== "random" && !form.key) {
    erro.value = "Informe a chave PIX.";
    return;
  }
  const typeUpper = String(type).toUpperCase();
  const ok = await financialStore.addPixKey({
    key: form.key,
    type: typeUpper,
  });

  if (ok) {
    const added = (financialStore.pixKeys as any[])?.find(
      (k) => k.key === form.key && k.type === type,
    );
    if (added)
      selectedPixKeyId.value = typeof added === "object" ? added.id : added;

    newPixForms.value.splice(index, 1);
    init({ message: "Chave PIX adicionada com sucesso.", color: "success" });
  } else if (financialStore.error) {
    erro.value = String(financialStore.error);
    init({ message: erro.value, color: "danger" });
  }
}
function extractId(v: any): string | number | null {
  if (v == null) return null;
  if (typeof v === "object") return v.id ?? v.value ?? null;
  return v;
}

const getTax = async () => {
  tax.value = await appStore.getTaxDolar(true);
};

const solicitarSaque = async () => {
  if (authStore.user === null) return;
  erro.value = "";

  if (valor.value <= 0) {
    erro.value = "Informe um valor válido.";
    return;
  }

  const keyId = extractId(selectedPixKeyId.value);
  if (!keyId) {
    erro.value = "Selecione ou cadastre uma chave PIX de destino.";
    return;
  }

  if (newPixForms.value.length > 0) {
    erro.value =
      "Salve ou remova as chaves em edição antes de confirmar o saque.";
    return;
  }

  try {
    const ok = await financialStore.requestWithdrawal({
      amount: valor.value,
      pixKeyId: selectedPixKeyId.value as any,
    });

    if (ok) {
      init({ message: "Saque solicitado com sucesso.", color: "success" });
      appStore.withdrawModal = false;
      return;
    }

    if (financialStore.error) {
      throw new Error(String(financialStore.error));
    }
  } catch (e: any) {
    erro.value = e?.message || "Erro ao solicitar saque.";
    init({ message: erro.value, color: "danger" });
  }
};

onMounted(async () => {
  await getTax();
  await financialStore.fetchPixKeys();
  if ((financialStore.pixKeys as any[])?.length) {
    selectedPixKeyId.value = (financialStore.pixKeys as any[])[0].id;
  }
});

const closeModal = () => {
  erro.value = "";
  withdrawalStore.isLoading = false;
  appStore.withdrawModal = false;
};
</script>
